import React, { FC, useCallback } from 'react';
import SelectionBubbleMenu from '../SelectionBubbleMenu';
import { useEditorContext } from '../../Editor/Context';
import {
  LinkBubbleMenuHandlerStorage,
  LinkMenuState,
} from '../../../extensions/extension-link-menu-handler/linkMenuHandler';
import ViewLinkMenuContent from './_partials/ViewLinkMenuContent';
import EditLinkMenuModal from './_partials/EditLinkMenuModal';

export interface Props {
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  isNew?: boolean;
}

const LinkBubbleMenu: FC<Props> = ({ scrollContainerRef, isNew }) => {
  const editor = useEditorContext();

  const onRemove = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.commands.closeLinkBubbleMenu();
    // Remove the link and place the cursor at the end of the link (which
    // requires "focus" to take effect)
    editor
      .chain()
      .unsetLink()
      .setTextSelection(editor.state.selection.to)
      .focus()
      .run();
    editor.storage.linkNode = undefined;
  }, [editor]);

  const onSave = useCallback(
    ({ text, link, initialText }) => {
      if (!editor) {
        return;
      }

      const { state } = editor;
      const { from, to, empty } = state.selection;

      if (!empty && from !== to && text === initialText) {
        // Selected range of content apply link to it
        // Empty of non range selection, insert the content and set the link
        editor
          .chain()
          .extendMarkRange('link') // if we inside a link expand to it
          .setLink({
            href: link,
          })
          .focus()
          .run();
      } else {
        // Empty of non range selection, insert the content and set the link
        editor
          .chain()
          // Make sure if we're updating a link, we update the link for the
          // full link "mark"
          .extendMarkRange('link')
          // Update the link href and its text content
          .insertContent({
            type: 'text',
            marks: [
              {
                type: 'link',
                attrs: {
                  href: link,
                },
              },
            ],
            text: text,
          })
          // Note that as of "@tiptap/extension-link" 2.0.0-beta.37 when
          // `autolink` is on (which we want), adding the link mark directly
          // via `insertContent` above wasn't sufficient for the link mark to
          // be applied (though specifying it above is still necessary), so we
          // insert the content there and call `setLink` separately here.
          // Unclear why this separate command is necessary, but it does the
          // trick.
          .setLink({
            href: link,
          })
          // Place the cursor at the end of the link (which requires "focus"
          // to take effect)
          .focus()
          .run();
      }

      editor.commands.closeLinkBubbleMenu();
    },
    [editor]
  );

  if (!editor) {
    return null;
  }

  if (!editor?.isEditable) {
    return null;
  }

  if (!('linkBubbleMenuHandler' in editor.storage)) {
    throw new Error(
      'You must add the LinkBubbleMenuHandler extension to the useEditor `extensions` array in order to use this component!'
    );
  }

  const handlerStorage = editor.storage
    .linkBubbleMenuHandler as LinkBubbleMenuHandlerStorage;

  // Update the menu step if the bubble menu state has changed
  const menuState = handlerStorage.state;
  const currentHref = handlerStorage.linkNodeAttrs?.href;

  // The view popover toolbar
  if (menuState === LinkMenuState.VIEW_LINK_DETAILS) {
    return (
      <SelectionBubbleMenu
        scrollContainerRef={scrollContainerRef}
        menuHeight={48}
        menuWidth={264}
        editor={editor}
        open={!!handlerStorage.linkNode}
        forceNode={handlerStorage.linkNode}
      >
        <ViewLinkMenuContent
          href={currentHref}
          editor={editor}
          onCancel={editor.commands.closeLinkBubbleMenu}
          onEdit={editor.commands.editLinkInBubbleMenu}
          onRemove={onRemove}
        />
      </SelectionBubbleMenu>
    );
  }

  // Edit using a modal
  if (menuState === LinkMenuState.EDIT_LINK) {
    return (
      <EditLinkMenuModal
        editor={editor}
        onCancel={editor.commands.closeLinkBubbleMenu}
        onSave={onSave}
        onRemove={onRemove}
      />
    );
  }

  return null;
};
LinkBubbleMenu.displayName = 'LinkBubbleMenu';

export default LinkBubbleMenu;
